import React, { useRef, useState } from "react";
import {
  Calendar,
  BarChart3,
  TrendingUp,
  PieChart,
  FileText,
  Download,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
} from "lucide-react";
import type { CampaignReportResponse } from "../../types";
import { exportElementAsPng } from "../../utils/exportUtils";
import { MetricCard } from "../../components/MetricCard";
import { DonutChart } from "../../components/DonutChart";
import { BarChart } from "../../components/BarChart";
import { formatReportDateLabel } from "../../utils/dateFormatting";

interface SlideLayoutProps {
  data: CampaignReportResponse;
  onNewUpload: () => void;
}

/**
 * PowerPoint-optimized slide layout with 16:9 aspect ratio.
 * Each section is a separate exportable slide.
 */
export const SlideLayout: React.FC<SlideLayoutProps> = ({ data }) => {
  const [exportingSlide, setExportingSlide] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  // Refs for each slide section
  const slide1Ref = useRef<HTMLDivElement>(null);
  const slide2Ref = useRef<HTMLDivElement>(null);
  const slide3Ref = useRef<HTMLDivElement>(null);
  const slide4Ref = useRef<HTMLDivElement>(null);
  const slide5Ref = useRef<HTMLDivElement>(null);

  const { summary, brands, campaigns, daily_metrics, report_type } = data;

  // Prepare chart data
  const totalSales = brands.reduce(
    (sum, b) => sum + parseFloat(b.total_sales),
    0,
  );
  const brandSalesData = brands.map((b) => ({
    brand_name: b.brand,
    units: parseFloat(b.total_sales),
    percentage: (parseFloat(b.total_sales) / totalSales) * 100,
  }));

  const roasLabels = brands.map((b) => b.brand);
  const roasValues = brands.map((b) => parseFloat(b.roas));

  const dailyLabels = daily_metrics.map((d) =>
    formatReportDateLabel(d.report_date),
  );
  const dailySalesDatasets = [
    {
      label: "Sales ($)",
      data: daily_metrics.map((d) => parseFloat(d.total_sales)),
      backgroundColor: "rgba(59, 130, 246, 0.8)",
    },
    {
      label: "Spend ($)",
      data: daily_metrics.map((d) => parseFloat(d.total_spend)),
      backgroundColor: "rgba(239, 68, 68, 0.8)",
    },
  ];

  const avgCTR = parseFloat(summary.average_ctr) * 100;
  const avgROAS = parseFloat(summary.average_roas);

  // Calculate performance insights
  const topPerformingBrand = brands.reduce((prev, current) =>
    parseFloat(current.roas) > parseFloat(prev.roas) ? current : prev,
  );
  const salesGrowthRate =
    daily_metrics.length > 1
      ? ((parseFloat(daily_metrics[daily_metrics.length - 1].total_sales) -
          parseFloat(daily_metrics[0].total_sales)) /
          parseFloat(daily_metrics[0].total_sales)) *
        100
      : 0;

  // Export individual slide as PNG
  const exportSlide = async (
    slideRef: React.RefObject<HTMLDivElement>,
    slideName: string,
  ): Promise<boolean> => {
    if (!slideRef.current) {
      setExportError(`Could not export ${slideName}: slide content is unavailable.`);
      return false;
    }

    setExportingSlide(slideName);
    setExportError(null);
    try {
      await exportElementAsPng(
        slideRef.current,
        `${slideName}-${new Date().toISOString().slice(0, 10)}.png`,
        {
          quality: 1.0,
          pixelRatio: 2,
          width: 1920,
          height: 1080,
          backgroundColor: "#ffffff",
        },
      );
      return true;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown export error";
      setExportError(errorMessage);
      return false;
    } finally {
      setExportingSlide(null);
    }
  };

  // Export all slides
  const exportAllSlides = async () => {
    const slides = [
      { ref: slide1Ref, name: "executive-summary" },
      { ref: slide2Ref, name: "brand-performance" },
      { ref: slide3Ref, name: "visual-analysis" },
      { ref: slide4Ref, name: "daily-trends" },
      { ref: slide5Ref, name: "campaign-details" },
    ];

    for (const slide of slides) {
      const exported = await exportSlide(slide.ref, slide.name);
      if (!exported) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
  };

  return (
    <div className="bg-gray-100 dark:bg-gray-950 py-6 px-4">
      {/* Global Header */}
      <div className="max-w-[1920px] mx-auto mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Campaign Performance Report
            </h1>
            <div className="flex items-center gap-4 mt-1 text-sm text-gray-600 dark:text-gray-400">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                {report_type === "search"
                  ? "Sponsored Products"
                  : "Display Ads"}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {summary.date_range_start} to {summary.date_range_end}
              </span>
              <span className="flex items-center gap-1">
                <BarChart3 className="w-3 h-3" />
                {data.records_processed} records
              </span>
            </div>
          </div>
          <button
            onClick={exportAllSlides}
            disabled={exportingSlide !== null}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg transition-colors font-medium shadow-sm flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export All Slides
          </button>
        </div>
      </div>
      {exportError && (
        <div
          className="max-w-[1920px] mx-auto mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
          role="alert"
        >
          {exportError}
        </div>
      )}

      {/* Slide Container */}
      <div className="max-w-[1920px] mx-auto space-y-6">
        {/* SLIDE 1: Executive Summary */}
        <div
          ref={slide1Ref}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-12 relative"
          style={{ aspectRatio: "16/9", minHeight: "540px" }}
        >
          <div className="flex justify-between items-start mb-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Executive Summary
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Overall performance metrics • {summary.total_days} days •{" "}
                {summary.campaign_count} campaigns
              </p>
            </div>
            <button
              onClick={() => exportSlide(slide1Ref, "executive-summary")}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              title="Export this slide"
            >
              <Download className="w-5 h-5" />
            </button>
          </div>

          {/* Key Metrics Grid - 3 across */}
          <div className="grid grid-cols-3 gap-6 mb-8">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl p-6 border border-blue-200 dark:border-blue-700">
              <div className="flex justify-between items-start mb-3">
                <div className="text-sm font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wide">
                  Total Sales
                </div>
                <ArrowUpRight className="w-5 h-5 text-green-500" />
              </div>
              <div className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
                $
                {parseFloat(summary.total_sales).toLocaleString("en-US", {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {summary.total_units.toLocaleString()} units sold
              </div>
            </div>

            <div className="bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
              <div className="flex justify-between items-start mb-3">
                <div className="text-sm font-medium text-red-600 dark:text-red-400 uppercase tracking-wide">
                  Total Spend
                </div>
                <Minus className="w-5 h-5 text-gray-400" />
              </div>
              <div className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
                $
                {parseFloat(summary.total_spend).toLocaleString("en-US", {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                $
                {(
                  parseFloat(summary.total_spend) / summary.total_days
                ).toLocaleString("en-US", {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}{" "}
                per day
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-xl p-6 border border-green-200 dark:border-green-700">
              <div className="flex justify-between items-start mb-3">
                <div className="text-sm font-medium text-green-600 dark:text-green-400 uppercase tracking-wide">
                  Return on Ad Spend
                </div>
                {avgROAS >= 4 ? (
                  <ArrowUpRight className="w-5 h-5 text-green-500" />
                ) : avgROAS >= 2 ? (
                  <Minus className="w-5 h-5 text-yellow-500" />
                ) : (
                  <ArrowDownRight className="w-5 h-5 text-red-500" />
                )}
              </div>
              <div className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
                {avgROAS.toFixed(2)}x
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {avgROAS >= 5
                  ? "Exceptional performance"
                  : avgROAS >= 3
                    ? "Strong performance"
                    : avgROAS >= 2
                      ? "Moderate performance"
                      : "Needs optimization"}
              </div>
            </div>
          </div>

          {/* Engagement Metrics - 3 across, more compact */}
          <div className="grid grid-cols-3 gap-6">
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-5 border border-gray-200 dark:border-gray-600">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                Total Impressions
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                {summary.total_impressions.toLocaleString()}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                {Math.round(
                  summary.total_impressions / summary.total_days,
                ).toLocaleString()}{" "}
                per day avg
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-5 border border-gray-200 dark:border-gray-600">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                Total Clicks
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                {summary.total_clicks.toLocaleString()}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                {Math.round(
                  summary.total_clicks / summary.total_days,
                ).toLocaleString()}{" "}
                per day avg
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-5 border border-gray-200 dark:border-gray-600">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                Click-Through Rate
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                {avgCTR.toFixed(3)}%
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                {avgCTR >= 1
                  ? "Above average"
                  : avgCTR >= 0.5
                    ? "Average"
                    : "Below average"}
              </div>
            </div>
          </div>

          {/* Key Insight Banner */}
          <div className="mt-8 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <TrendingUp className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
              <div>
                <div className="font-semibold text-blue-900 dark:text-blue-300 mb-1">
                  Key Insight
                </div>
                <div className="text-sm text-blue-800 dark:text-blue-400">
                  <strong>{topPerformingBrand.brand}</strong> is your top
                  performer with a{" "}
                  <strong>
                    {parseFloat(topPerformingBrand.roas).toFixed(2)}x ROAS
                  </strong>
                  , generating{" "}
                  <strong>
                    $
                    {parseFloat(
                      topPerformingBrand.total_sales,
                    ).toLocaleString()}
                  </strong>{" "}
                  in sales.
                  {salesGrowthRate !== 0 && (
                    <>
                      {" "}
                      Daily sales are{" "}
                      {salesGrowthRate > 0
                        ? "trending up"
                        : "declining"} by{" "}
                      <strong>{Math.abs(salesGrowthRate).toFixed(1)}%</strong>{" "}
                      over the period.
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SLIDE 2: Performance by Brand */}
        <div
          ref={slide2Ref}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-12 relative"
          style={{ aspectRatio: "16/9", minHeight: "540px" }}
        >
          <div className="flex justify-between items-start mb-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Performance by Brand
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Detailed breakdown of {summary.brand_count} active brands
              </p>
            </div>
            <button
              onClick={() => exportSlide(slide2Ref, "brand-performance")}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              title="Export this slide"
            >
              <Download className="w-5 h-5" />
            </button>
          </div>

          {/* Brand Cards - 3 across with tighter spacing */}
          <div className="grid grid-cols-3 gap-5">
            {brands.map((brand) => {
              const brandROAS = parseFloat(brand.roas);
              const brandCTR =
                (brand.total_clicks / brand.total_impressions) * 100;
              const brandSalesShare =
                (parseFloat(brand.total_sales) / totalSales) * 100;

              return (
                <div
                  key={brand.brand}
                  className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-700 dark:to-gray-800 rounded-lg border-2 border-gray-200 dark:border-gray-600 p-5 hover:shadow-lg transition-all"
                >
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                      {brand.brand}
                    </h3>
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${
                        brandROAS >= 5
                          ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                          : brandROAS >= 3
                            ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                            : brandROAS >= 2
                              ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                              : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                      }`}
                    >
                      {brandROAS.toFixed(2)}x ROAS
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                        Sales
                      </div>
                      <div className="text-xl font-bold text-gray-900 dark:text-white">
                        $
                        {parseFloat(brand.total_sales).toLocaleString("en-US", {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        {brandSalesShare.toFixed(1)}% of total
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                        Spend
                      </div>
                      <div className="text-xl font-bold text-gray-900 dark:text-white">
                        $
                        {parseFloat(brand.total_spend).toLocaleString("en-US", {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        {brand.campaign_count} campaigns
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 pt-4 border-t border-gray-200 dark:border-gray-600">
                    <div className="text-center">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                        Units
                      </div>
                      <div className="text-sm font-semibold text-gray-900 dark:text-white">
                        {brand.total_units.toLocaleString()}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                        CTR
                      </div>
                      <div className="text-sm font-semibold text-gray-900 dark:text-white">
                        {brandCTR.toFixed(2)}%
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                        Impr.
                      </div>
                      <div className="text-sm font-semibold text-gray-900 dark:text-white">
                        {(brand.total_impressions / 1000).toFixed(0)}K
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Comparison Insight */}
          <div className="mt-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <BarChart3 className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />
              <div className="text-sm text-amber-900 dark:text-amber-300">
                <strong>Performance Spread:</strong> ROAS ranges from{" "}
                {Math.min(...brands.map((b) => parseFloat(b.roas))).toFixed(2)}x
                to{" "}
                {Math.max(...brands.map((b) => parseFloat(b.roas))).toFixed(2)}x
                across brands. Consider reallocating budget from lower
                performers to maximize overall efficiency.
              </div>
            </div>
          </div>
        </div>

        {/* SLIDE 3: Visual Analysis */}
        <div
          ref={slide3Ref}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-12 relative"
          style={{ aspectRatio: "16/9", minHeight: "540px" }}
        >
          <div className="flex justify-between items-start mb-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Visual Analysis
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Sales distribution and ROAS comparison
              </p>
            </div>
            <button
              onClick={() => exportSlide(slide3Ref, "visual-analysis")}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              title="Export this slide"
            >
              <Download className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-8 h-[400px]">
            {/* Sales Distribution */}
            <div className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-700 dark:to-gray-800 rounded-xl p-6 border-2 border-gray-200 dark:border-gray-600">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Sales Distribution by Brand
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Total: $
                {parseFloat(summary.total_sales).toLocaleString("en-US", {
                  minimumFractionDigits: 0,
                })}
              </p>
              <div className="h-[280px]">
                <DonutChart data={brandSalesData} />
              </div>
            </div>

            {/* ROAS Comparison */}
            <div className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-700 dark:to-gray-800 rounded-xl p-6 border-2 border-gray-200 dark:border-gray-600">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Return on Ad Spend by Brand
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Target: 3.0x+ • Industry benchmark
              </p>
              <div className="h-[280px]">
                <BarChart
                  labels={roasLabels}
                  datasets={[
                    {
                      label: "ROAS",
                      data: roasValues,
                      backgroundColor: roasValues.map((val) =>
                        val >= 5
                          ? "rgba(34, 197, 94, 0.8)"
                          : val >= 3
                            ? "rgba(59, 130, 246, 0.8)"
                            : val >= 2
                              ? "rgba(251, 191, 36, 0.8)"
                              : "rgba(239, 68, 68, 0.8)",
                      ),
                    },
                  ]}
                  horizontal={true}
                />
              </div>
            </div>
          </div>
        </div>

        {/* SLIDE 4: Daily Performance Trend */}
        <div
          ref={slide4Ref}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-12 relative"
          style={{ aspectRatio: "16/9", minHeight: "540px" }}
        >
          <div className="flex justify-between items-start mb-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Daily Performance Trend
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Sales vs Spend over {summary.total_days} days • Identify
                patterns and optimize timing
              </p>
            </div>
            <button
              onClick={() => exportSlide(slide4Ref, "daily-trends")}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              title="Export this slide"
            >
              <Download className="w-5 h-5" />
            </button>
          </div>

          <div className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-700 dark:to-gray-800 rounded-xl p-6 border-2 border-gray-200 dark:border-gray-600">
            <div className="h-[380px]">
              <BarChart
                labels={dailyLabels}
                datasets={dailySalesDatasets}
                title="Sales vs Spend ($)"
                stacked={false}
                horizontal={false}
                showLegend={true}
              />
            </div>
          </div>

          {/* Trend Analysis */}
          <div className="mt-6 grid grid-cols-3 gap-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
              <div className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-1">
                Avg Daily Sales
              </div>
              <div className="text-xl font-bold text-gray-900 dark:text-white">
                $
                {(
                  parseFloat(summary.total_sales) / summary.total_days
                ).toLocaleString("en-US", {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </div>
            </div>
            <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 border border-red-200 dark:border-red-700">
              <div className="text-xs font-medium text-red-600 dark:text-red-400 uppercase tracking-wide mb-1">
                Avg Daily Spend
              </div>
              <div className="text-xl font-bold text-gray-900 dark:text-white">
                $
                {(
                  parseFloat(summary.total_spend) / summary.total_days
                ).toLocaleString("en-US", {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </div>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-700">
              <div className="text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wide mb-1">
                Period Trend
              </div>
              <div className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                {salesGrowthRate > 0 ? (
                  <>
                    <ArrowUpRight className="w-5 h-5 text-green-500" />+
                    {salesGrowthRate.toFixed(1)}%
                  </>
                ) : salesGrowthRate < 0 ? (
                  <>
                    <ArrowDownRight className="w-5 h-5 text-red-500" />
                    {salesGrowthRate.toFixed(1)}%
                  </>
                ) : (
                  <>
                    <Minus className="w-5 h-5 text-gray-400" />
                    Flat
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* SLIDE 5: Campaign Details */}
        <div
          ref={slide5Ref}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-12 relative"
          style={{ aspectRatio: "16/9", minHeight: "540px" }}
        >
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Campaign Details
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                {summary.campaign_count} active campaigns ranked by sales
                performance
              </p>
            </div>
            <button
              onClick={() => exportSlide(slide5Ref, "campaign-details")}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              title="Export this slide"
            >
              <Download className="w-5 h-5" />
            </button>
          </div>

          <div className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-700 dark:to-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-600 overflow-hidden">
            <div className="overflow-x-auto" style={{ maxHeight: "420px" }}>
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                <thead className="bg-gray-100 dark:bg-gray-900 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                      Campaign
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                      Brand
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                      Product
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                      Sales
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                      Spend
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                      ROAS
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                      Units
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                      Days
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {campaigns
                    .sort(
                      (a, b) =>
                        parseFloat(b.total_sales) - parseFloat(a.total_sales),
                    )
                    .map((campaign, index) => {
                      const campaignROAS = parseFloat(campaign.roas);
                      return (
                        <tr
                          key={campaign.campaign_name}
                          className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                            index < 3 ? "bg-blue-50/30 dark:bg-blue-900/10" : ""
                          }`}
                        >
                          <td
                            className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white max-w-xs truncate"
                            title={campaign.campaign_name}
                          >
                            {index < 3 && (
                              <span className="inline-flex items-center justify-center w-5 h-5 mr-2 text-xs font-bold text-white bg-blue-600 rounded-full">
                                {index + 1}
                              </span>
                            )}
                            {campaign.campaign_name}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                            {campaign.brand}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                            {campaign.product}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-semibold text-gray-900 dark:text-white">
                            $
                            {parseFloat(campaign.total_sales).toLocaleString(
                              "en-US",
                              { minimumFractionDigits: 0 },
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-700 dark:text-gray-300">
                            $
                            {parseFloat(campaign.total_spend).toLocaleString(
                              "en-US",
                              { minimumFractionDigits: 0 },
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                            <span
                              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${
                                campaignROAS >= 5
                                  ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                  : campaignROAS >= 3
                                    ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                                    : campaignROAS >= 2
                                      ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                                      : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                              }`}
                            >
                              {campaignROAS.toFixed(2)}x
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-700 dark:text-gray-300">
                            {campaign.total_units.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-700 dark:text-gray-300">
                            {campaign.days_active}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
